import type { RunAggregateProjectionV2, WebTestRun } from '@blume/web-test-model'
import { CircleCheck, CircleHelp, CircleX, Eye, LoaderCircle, ServerCrash } from 'lucide-react'

import {
  classificationMeta,
  runStateLabel,
  runStatusMeta,
  runVerdict,
  StatusInline,
  type StatusMeta,
  type Tone
} from '@/design/status'
import {
  aggregateProgress,
  aggregateStatuses,
  groupRuns,
  progressFromDurableAggregate,
  type AggregateProgress,
  type AggregateStatus
} from '@/logic'

/**
 * Shared presenters for the two durable dashboard entities — suites and
 * scenarios — used by both the aggregate indexes and the entity detail
 * pages, so an index card and the page it links to can never disagree
 * about what a suite or scenario currently looks like.
 */

/** Canonical verifier grouping bucket for runs that started outside any suite. */
export const independentSuiteKey = 'independent'

export function suiteHref(key: string): string {
  return `#/suites/${encodeURIComponent(key)}`
}

export function scenarioHref(scenarioId: string): string {
  return `#/scenarios/${encodeURIComponent(scenarioId)}`
}

export interface SuiteSummary {
  key: string
  label: string
  /** Runs of this suite that are actually loaded on this page. */
  runs: WebTestRun[]
  progress: AggregateProgress
  updatedAt?: string
}

export function suiteSummaries(
  runs: WebTestRun[],
  aggregates?: RunAggregateProjectionV2[]
): SuiteSummary[] {
  if (aggregates) {
    const runsById = new Map(runs.map((run) => [run.id, run]))
    return aggregates.map((aggregate) => ({
      key: aggregate.key,
      label: aggregate.label,
      runs: aggregate.runIds.flatMap((id) => {
        const run = runsById.get(id)
        return run ? [run] : []
      }),
      progress: progressFromDurableAggregate(aggregate),
      updatedAt: aggregate.updatedAt
    }))
  }
  return groupRuns(
    runs,
    (run) => run.suiteRunId ?? independentSuiteKey,
    (run) => (run.suiteRunId ? run.suiteRunId : 'Ad-hoc runs (no suite)')
  ).map((group) => ({
    ...group,
    progress: aggregateProgress(group.runs),
    updatedAt: [...group.runs].sort(
      (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    )[0]?.createdAt
  }))
}

export interface ScenarioSummary {
  scenarioId: string
  latest?: WebTestRun
  progress: AggregateProgress
  runCount: number
  /** Runs of this scenario that are actually loaded on this page. */
  loadedRuns: WebTestRun[]
  /** How many of this scenario's runs are actually loaded on this page. */
  loadedCount: number
  /** Distinct suite-run identifiers observed among the loaded runs. */
  suiteRunIds: string[]
  /** Latest suite context observed for this scenario (not a membership claim). */
  latestSuiteRunId?: string
  /** Whether any loaded run started outside a suite. */
  hasAdHoc: boolean
  targets: string[]
  updatedAt: string
}

export function attentionRank(progress: AggregateProgress): number {
  if (progress.counts.failed > 0) return 0
  if (progress.counts['needs-review'] > 0 || progress.counts['timed-out'] > 0) return 1
  if (progress.counts.active > 0) return 2
  if (progress.counts['infrastructure-error'] > 0) return 3
  return 4
}

export function aggregateVerdict(progress: AggregateProgress): StatusMeta {
  if (progress.counts.failed > 0 || progress.counts['timed-out'] > 0) {
    return { label: 'Failed', icon: CircleX, tone: 'fail' }
  }
  if (progress.counts['needs-review'] > 0) {
    return { label: 'Needs review', icon: Eye, tone: 'warn' }
  }
  if (progress.counts.active > 0) {
    return { label: 'Verifying', icon: LoaderCircle, tone: 'info' }
  }
  if (progress.counts['infrastructure-error'] > 0) {
    return { label: 'Infrastructure error', icon: ServerCrash, tone: 'infra' }
  }
  if (progress.counts.passed > 0) {
    return { label: 'Passed', icon: CircleCheck, tone: 'pass' }
  }
  return { label: 'No verdict', icon: CircleHelp, tone: 'neutral' }
}

function summarizeSuiteMembership(
  loadedRuns: WebTestRun[]
): Pick<ScenarioSummary, 'loadedRuns' | 'loadedCount' | 'suiteRunIds' | 'hasAdHoc'> {
  return {
    loadedRuns,
    loadedCount: loadedRuns.length,
    suiteRunIds: [
      ...new Set(loadedRuns.flatMap((run) => (run.suiteRunId ? [run.suiteRunId] : [])))
    ].sort((left, right) => left.localeCompare(right)),
    hasAdHoc: loadedRuns.some((run) => !run.suiteRunId)
  }
}

export function scenarioSummaries(
  runs: WebTestRun[],
  aggregates?: RunAggregateProjectionV2[]
): ScenarioSummary[] {
  if (aggregates) {
    const runsById = new Map(runs.map((run) => [run.id, run]))
    return aggregates
      .map((aggregate) => {
        const aggregateRuns = aggregate.runIds.flatMap((id) => {
          const run = runsById.get(id)
          return run ? [run] : []
        })
        const latestRunId = aggregate.latestRunId
        return {
          scenarioId: aggregate.key,
          ...(latestRunId && runsById.get(latestRunId)
            ? { latest: runsById.get(latestRunId)! }
            : {}),
          progress: progressFromDurableAggregate(aggregate),
          runCount: aggregate.total,
          ...summarizeSuiteMembership(aggregateRuns),
          ...(latestRunId && runsById.get(latestRunId)?.suiteRunId
            ? { latestSuiteRunId: runsById.get(latestRunId)!.suiteRunId }
            : {}),
          targets: [...new Set(aggregateRuns.map((run) => run.targetKind))],
          updatedAt: aggregate.updatedAt
        }
      })
      .sort(
        (left, right) =>
          attentionRank(left.progress) - attentionRank(right.progress) ||
          left.scenarioId.localeCompare(right.scenarioId)
      )
  }
  const byScenario = new Map<string, WebTestRun[]>()
  for (const run of runs) {
    byScenario.set(run.scenarioId, [...(byScenario.get(run.scenarioId) ?? []), run])
  }
  return [...byScenario.entries()]
    .map(([scenarioId, scenarioRuns]) => {
      const sorted = [...scenarioRuns].sort(
        (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
      )
      const latest = sorted[0]!
      return {
        scenarioId,
        latest,
        progress: aggregateProgress(scenarioRuns),
        runCount: scenarioRuns.length,
        ...summarizeSuiteMembership(scenarioRuns),
        ...(latest.suiteRunId ? { latestSuiteRunId: latest.suiteRunId } : {}),
        targets: [...new Set(scenarioRuns.map((run) => run.targetKind))],
        updatedAt: latest.createdAt
      }
    })
    .sort(
      (left, right) =>
        attentionRank(left.progress) - attentionRank(right.progress) ||
        left.scenarioId.localeCompare(right.scenarioId)
    )
}

/**
 * The single status a scenario leads with: the latest run's verdict, refined
 * to its failure classification when that is the more precise statement
 * ("Product failure" already says failed). Icon and label always travel
 * together — status is never color alone.
 */
export function scenarioStatus(scenario: ScenarioSummary): { meta: StatusMeta; active: boolean } {
  const verdict = scenario.latest
    ? runVerdict(scenario.latest)
    : { ...aggregateVerdict(scenario.progress), active: scenario.progress.counts.active > 0 }
  const classification = scenario.latest?.classification
    ? classificationMeta[scenario.latest.classification]
    : undefined
  const meta =
    classification &&
    (scenario.latest?.status === 'failed' || classification.label === verdict.label)
      ? classification
      : verdict
  return { meta, active: verdict.active }
}

export function aggregateStatusMeta(status: AggregateStatus): StatusMeta {
  if (status === 'active') {
    return { label: runStateLabel.running, icon: LoaderCircle, tone: 'info' }
  }
  return runStatusMeta[status]
}

const barTone: Record<Tone, string> = {
  pass: 'bg-status-pass',
  fail: 'bg-status-fail',
  warn: 'bg-status-warn',
  info: 'bg-status-info',
  infra: 'bg-status-infra',
  neutral: 'bg-status-neutral'
}

/**
 * A suite's health at a glance: a thin proportional bar plus a written legend
 * of every non-zero outcome, so the color is never the only carrier.
 */
export function StatusDistribution({ progress }: { progress: AggregateProgress }) {
  return (
    <>
      <div
        aria-hidden
        className="flex h-1.5 max-w-md overflow-hidden rounded-full bg-muted opacity-80"
      >
        {aggregateStatuses.map((status) => {
          const count = progress.counts[status]
          if (count === 0) return null
          const meta = aggregateStatusMeta(status)
          return <span key={status} className={barTone[meta.tone]} style={{ flexGrow: count }} />
        })}
      </div>
      <ul className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1">
        {aggregateStatuses.map((status) => {
          const count = progress.counts[status]
          if (count === 0) return null
          const meta = aggregateStatusMeta(status)
          return (
            <li key={status} className="flex items-center gap-1.5">
              <StatusInline meta={meta} />
              <span className="text-[13px] text-muted-foreground tabular-nums">{count}</span>
            </li>
          )
        })}
      </ul>
    </>
  )
}
